import React from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { BackButton } from '~/components/BackButton';
import { Card } from '~/components/Card';
import { ActivityFeed, GapFeed, PatientInfo, Statistics } from '~/components/Patient';
import {
    PatientContext,
    PatientContextType,
    patientServiceEvents,
    usePatientService,
} from '~/services';
import { useServiceSubscribe } from '~/util';

export interface PatientPageStatusType {
    [key: string]: number;
}

interface PatientProps {
    reportYear: string;
}
export function Patient({ reportYear }: PatientProps) {
    const { getPatient, loadPatient } = usePatientService();
    const { setPatientGmpi } = React.useContext(PatientContext) as PatientContextType;
    const location = useLocation<{
        page?: string;
        status?: {
            risk_gaps?: number;
            quality_gaps?: number;
            foc_gaps?: number;
        };
    }>();
    const match = useRouteMatch<{ id: string }>();
    const [patient, setPatient] = React.useState<PatientType | null>(null);
    const [page, setPage] = React.useState(
        location?.state?.page ? location.state.page : 'risk_gaps'
    );
    const [status, setPageStatus] = React.useState<PatientPageStatusType>({
        // if status passed in from redirect use that, else 1
        risk_gaps: location?.state?.status?.risk_gaps || 1,
        foc_gaps: location?.state?.status?.foc_gaps || 1,
        quality_gaps: location?.state?.status?.quality_gaps || 1,
    });
    const [activityFeedTab, setActivityFeedTab] = React.useState('healthhx');
    const [missingReportYear, setMissingReportYear] = React.useState(false);
    const [healthHxVal, setHealthHxVal] = React.useState<string | number>(0);
    const initialLoad = React.useRef(true);

    React.useEffect(() => {
        (async () => {
            try {
                await loadPatient(Number(match.params.id), !initialLoad.current);
                initialLoad.current = false;
                setMissingReportYear(false);
            } catch (error) {
                if (error?.response?.status === 404) {
                    setMissingReportYear(true);
                    setPatient(null);
                }
            }
        })();
    }, [loadPatient, match.params.id, reportYear]);

    const updatePatient = React.useCallback(() => {
        const p = getPatient(Number(match.params.id));
        if (p) {
            setPatient({ ...p });
            setPatientGmpi(p.gmpi ?? null);
        }
    }, [getPatient, match.params.id, setPatientGmpi]);
    useServiceSubscribe(patientServiceEvents.patient_updated, updatePatient);

    function setStatus(newPage: string, newStatus: string | number) {
        setPage(newPage);
        setPageStatus((prev) => ({
            ...prev,
            [newPage]: Number(newStatus),
        }));
    }

    function scrollToItem(item: string) {
        document.getElementById('activity-feed')?.scrollIntoView();
        if (activityFeedTab !== 'healthhx') {
            // Delay the state update so that the page has time to scroll first
            setTimeout(() => {
                setActivityFeedTab(item);
            }, 300);
        }
    }

    if (missingReportYear) {
        return (
            <Card
                title={
                    <span>
                        <BackButton />
                        Patient Record
                    </span>
                }
                className="mb-3"
            >
                <div className="h4 text-center">
                    Sorry, this patient&apos;s record is not available for the currently selected
                    report year.
                </div>
            </Card>
        );
    }

    if (patient === null) return null;

    return (
        <div>
            <Statistics
                patientId={patient.id}
                page={page}
                status={status[page] as number}
                setStatus={setStatus}
                healthHxVal={healthHxVal}
                scrollToItem={scrollToItem}
            />
            <PatientInfo patient={patient} />
            <GapFeed
                page={page}
                setStatus={setStatus}
                setPage={setPage}
                patient={patient}
                status={status}
            />
            <ActivityFeed
                patient={patient}
                reportYear={reportYear}
                setHealthHxVal={setHealthHxVal}
                tab={activityFeedTab}
                setTab={setActivityFeedTab}
            />
        </div>
    );
}
